import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MonitorPlay, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { HeadcountCard, AbsenteeDialog } from '@/components/staffAttendance/HeadcountCard';
import { matchStaff } from '@/components/staffAttendance/StaffSearch';
import { CollarSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { staffAttApi, errMsg, fmtTime, todayIST } from '@/services/staffAttendanceApi';

const REFRESH_MS = 60000;

/** Loads the headcount board and refreshes it every 60 s. */
export const useHeadcount = (params) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const key = JSON.stringify(params);
  const load = useCallback(() => {
    const n = ++seq.current;
    setLoading(true);
    return staffAttApi.headcount(JSON.parse(key)).then((d) => n === seq.current && setData(d))
      .catch((e) => toast.error(errMsg(e, 'Failed to load headcount'))).finally(() => n === seq.current && setLoading(false));
  }, [key]);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);
  return { data, loading, load };
};

export const TotalsStrip = ({ t, tv }) => {
  const items = [
    ['In Roll', t.in_roll, tv ? 'text-white' : 'text-gray-900', 'in-roll'],
    ['Present', t.present, 'text-emerald-500', 'present'],
    ['Day shift', t.day_only, 'text-amber-500', 'day-only'],
    ['Day + Night', t.day_night, tv ? 'text-violet-300' : 'text-violet-600', 'day-night'],
    ['Night shift', t.night_only, tv ? 'text-indigo-300' : 'text-indigo-600', 'night-only'],
    ['Waiting approval', t.pending, 'text-amber-500', 'pending'],
    [`Absent · ${t.absent_pct}%`, t.absent, 'text-red-500', 'absent'],
    ['Late', t.late, 'text-orange-500', 'late'],
    ['Sections over limit', t.alert_sections, 'text-red-500', 'alerts'],
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
      {items.map(([label, v, tone, id]) => (
        <div key={id} className={`rounded-xl p-3 ${tv ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          <div className={`${tv ? 'text-sm text-slate-400' : 'text-[11px] text-gray-500'} uppercase tracking-wide`}>{label}</div>
          <div className={`${tv ? 'text-4xl' : 'text-2xl'} font-bold tabular-nums ${tone}`} data-testid={`headcount-total-${id}`}>{v}</div>
        </div>
      ))}
    </div>
  );
};

export const HeadcountGrid = ({ data, tv }) => {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className={`grid gap-4 ${tv ? 'grid-cols-2 lg:grid-cols-4 xl:grid-cols-5' : 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`} data-testid="headcount-grid">
        {data.sections.map((c) => <HeadcountCard key={c.key || 'none'} c={c} tv={tv} onOpen={setOpen} />)}
      </div>
      {data.sections.length === 0 && <p className="text-center text-gray-400 py-10" data-testid="headcount-empty">No In Roll staff in your sections.</p>}
      <AbsenteeDialog card={open} onClose={() => setOpen(null)} />
    </>
  );
};

const REASON = { no_punch: 'No punch yet', rejected: 'Punch rejected' };

/** Employee search on the board: matching absentees across all sections. */
const AbsentMatches = ({ data, q }) => {
  const hits = data.sections.flatMap((c) => c.absentees.filter((a) => matchStaff(q, a.staff_name, a.staff_id)).map((a) => ({ ...a, section: c.section || 'No section' })));
  return (
    <div className="rounded-xl bg-white shadow-sm p-3 text-sm" data-testid="headcount-search-result">
      {hits.length === 0 ? <span className="text-gray-500">No absent staff match “{q.trim()}” — they have checked in, or are not In Roll for this day.</span> : (
        <div className="space-y-1">
          <div className="font-semibold text-red-600">{hits.length} absent staff match “{q.trim()}”</div>
          {hits.map((a) => (
            <div key={a.staff_id} className="flex flex-wrap items-center gap-2" data-testid={`headcount-search-hit-${a.staff_id}`}>
              <span className="font-mono text-gray-500">{a.staff_id}</span><span className="font-medium">{a.staff_name}</span>
              <span className="text-gray-500">· {a.section}</span><span className="text-xs rounded-full bg-gray-100 px-2 py-0.5">{REASON[a.reason]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** On floor vs absent per White / Blue / Not set (click = filter the board). */
export const HeadcountCollarTiles = ({ data, collar, onPick, tv }) => (
  <CollarTiles split={data.collar_split} value={collar} onPick={onPick} tv={tv} metric="On floor" testId="headcount-collar-tiles"
    sub={(s) => `of ${s.staff} · ${s.absent} absent`} />
);

/** Hub tab: filters + totals + section cards. */
export const HeadcountBoard = ({ search = '' }) => {
  const [date, setDate] = useState('');
  const [workerType, setWorkerType] = useState('');
  const [collar, setCollar] = useState('');
  const { data, loading, load } = useHeadcount({ ...(date ? { date } : {}), ...(workerType ? { worker_type: workerType } : {}), ...(collar ? { collar } : {}) });
  const tvQ = new URLSearchParams({ ...(workerType ? { worker_type: workerType } : {}), ...(collar ? { collar } : {}) }).toString();
  const tvUrl = `/attendance/headcount-tv${tvQ ? `?${tvQ}` : ''}`;
  return (
    <div className="space-y-4" data-testid="headcount-board">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date || data?.date || ''} max={todayIST()} onChange={(e) => setDate(e.target.value)} className="w-[170px] bg-white" data-testid="headcount-date" />
        <select value={workerType} onChange={(e) => setWorkerType(e.target.value)} className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="headcount-worker-type">
          <option value="">Regular + Contract</option><option value="regular">Regular</option><option value="contract">Contract</option>
        </select>
        <CollarSelect value={collar} onChange={setCollar} testId="headcount-collar-select" />
        <Button variant="outline" onClick={load} disabled={loading} data-testid="headcount-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        <Button onClick={() => window.open(tvUrl, '_blank', 'noopener')} className="bg-slate-800 hover:bg-slate-900" data-testid="headcount-tv-btn"><MonitorPlay className="w-4 h-4 mr-1" />TV mode</Button>
        {data && <span className="ml-auto text-xs text-gray-500" data-testid="headcount-updated">Red = absent above {data.threshold_pct}% · updated {fmtTime(data.generated_at)} · auto-refresh 60 s</span>}
      </div>
      {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" /> : (
        <><TotalsStrip t={data.totals} /><HeadcountCollarTiles data={data} collar={collar} onPick={setCollar} />{search.trim() && <AbsentMatches data={data} q={search} />}<HeadcountGrid data={data} /></>
      )}
    </div>
  );
};

export default HeadcountBoard;
